import React, { useCallback, useEffect, useState } from "react";
import { Eye, Pencil, RotateCcw, ScrollText, ShieldCheck, Trash2, Users as UsersIcon, UserX } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { Spinner, EmptyState } from "../common";
import { api, errMsg, fmtDateIST, fmtDateTime } from "../../lib/api";
import { PaginationBar } from "../PaginationBar";
import { RestoreWindowBadge } from "../../pages/admin/CitizensPage";

/**
 * Phase 215 — Super Admin → Tenant Config → "Citizens" tab for the selected municipality.
 *  • View / Edit / Delete switches for the Municipal Admin's Citizens module (defaults: View ON, Edit ON, Delete OFF).
 *  • Deleted Citizens of the municipality — restore (also after the FY window) or permanently delete (typed confirmation).
 *  • Recent citizen audit events (permission changes, delete / restore / permanent delete).
 */

const SWITCHES = [
  { key: "view", label: "View citizens", icon: Eye, help: "Shows the Citizens menu and page to Municipal Admins. OFF hides the whole module (export included)." },
  { key: "edit", label: "Edit citizens", icon: Pencil, help: "Edit name / email / mobile, activate / deactivate, reset password and menu access." },
  { key: "delete", label: "Delete & restore citizens", icon: Trash2, help: "Soft delete into Deleted Citizens and restore until the end of that financial year. OFF by default — only you can delete / restore." },
];

export function SuperAdminCitizensPanel({ code, testIdPrefix = "sa-citizens" }) {
  const [meta, setMeta] = useState(null);
  const [saving, setSaving] = useState(null);
  const [deleted, setDeleted] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [audit, setAudit] = useState(null);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [purgeTarget, setPurgeTarget] = useState(null);
  const [purgeText, setPurgeText] = useState("");
  const [busy, setBusy] = useState(false);

  const loadMeta = useCallback(() => {
    if (!code) return;
    api.get(`/platform/municipalities/${code}/citizen-access`).then((r) => setMeta(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [code]);
  const loadDeleted = useCallback(() => {
    if (!code) return;
    const params = { page, limit, sort: "deleted_recent" };
    if (search.trim()) params.search = search.trim();
    api.get(`/t/${code}/citizens/deleted`, { params }).then((r) => setDeleted(r.data)).catch((e) => { toast.error(errMsg(e)); setDeleted({ items: [], total: 0 }); });
  }, [code, page, limit, search]);
  const loadAudit = useCallback(() => {
    if (!code) return;
    api.get(`/t/${code}/citizens/audit`, { params: { limit: 15 } }).then((r) => setAudit(r.data.items || [])).catch(() => setAudit([]));
  }, [code]);

  useEffect(() => { setMeta(null); setDeleted(null); setAudit(null); setPage(1); setSearch(""); }, [code]);
  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadDeleted(); }, [loadDeleted]);
  useEffect(() => { loadAudit(); }, [loadAudit]);
  const refreshAll = () => { loadMeta(); loadDeleted(); loadAudit(); };

  const flip = async (key, value) => {
    setSaving(key);
    try {
      const r = await api.put(`/platform/municipalities/${code}/citizen-access`, { [key]: value });
      setMeta((m) => ({ ...(m || {}), access: r.data.access, updated_at: r.data.updated_at, updated_by: r.data.updated_by }));
      toast.success(`${SWITCHES.find((s) => s.key === key)?.label} ${value ? "enabled" : "disabled"} for ${code.toUpperCase()}`);
      loadAudit();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(null); }
  };

  const confirmRestore = async () => {
    if (!restoreTarget) return;
    setBusy(true);
    try {
      await api.post(`/t/${code}/citizens/${restoreTarget.id}/restore`);
      toast.success(`${restoreTarget.name} restored`);
      setRestoreTarget(null);
      refreshAll();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const confirmPurge = async () => {
    if (!purgeTarget || purgeText.trim().toUpperCase() !== "DELETE") return;
    setBusy(true);
    try {
      const r = await api.delete(`/t/${code}/citizens/${purgeTarget.id}/permanent`);
      toast.success(`${purgeTarget.name} permanently deleted — ${r.data.unlinked_properties} property link${r.data.unlinked_properties === 1 ? "" : "s"} cleared`);
      setPurgeTarget(null); setPurgeText("");
      refreshAll();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!meta) return <Spinner />;
  const access = meta.access || {};

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-panel`}>
      <Card className="shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-heading flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Citizen permissions — {meta.municipality?.name || code}</CardTitle>
          <CardDescription className="text-xs">
            What the Municipal Admin may do in the Citizens module. Defaults for a new municipality: View ON, Edit ON, Delete OFF.
            {meta.counts && <> · <span className="tabular-nums">{meta.counts.citizens}</span> citizen account{meta.counts.citizens === 1 ? "" : "s"}, <span className="tabular-nums">{meta.counts.deleted}</span> deleted</>}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 pt-2 space-y-2">
          {SWITCHES.map(({ key, label, icon: Icon, help }) => (
            <div key={key} className="flex items-start justify-between gap-3 rounded-lg border p-3" data-testid={`${testIdPrefix}-switch-row-${key}`}>
              <div className="flex items-start gap-2 min-w-0">
                <span className="h-8 w-8 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0"><Icon className="h-4 w-4" /></span>
                <div className="min-w-0">
                  <p className="text-sm font-medium flex items-center gap-2">{label}
                    <Badge variant="outline" className={`text-[10px] font-normal ${access[key] ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "bg-muted text-muted-foreground"}`}
                      data-testid={`${testIdPrefix}-state-${key}`}>{access[key] ? "ON" : "OFF"}</Badge>
                  </p>
                  <p className="text-xs text-muted-foreground">{help}</p>
                </div>
              </div>
              <Switch checked={!!access[key]} disabled={saving === key} onCheckedChange={(v) => flip(key, v)} data-testid={`${testIdPrefix}-switch-${key}`} aria-label={label} />
            </div>
          ))}
          <p className="text-[11px] text-muted-foreground" data-testid={`${testIdPrefix}-updated`}>
            {meta.updated_at ? <>Last changed {fmtDateTime(meta.updated_at)} by {meta.updated_by}</> : "Not changed yet — platform defaults apply."}
            {" "}Every change is written to the municipality's Audit Trail.
          </p>
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-heading flex items-center gap-2"><UserX className="h-4 w-4" /> Deleted citizens</CardTitle>
          <CardDescription className="text-xs">
            Accounts the municipality deleted. The Municipal Admin can restore until the end of that financial year; you can restore at any time or permanently delete
            (removes the login and clears the property link — property records, demands and receipts are untouched).
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 pt-2 space-y-3">
          <Input placeholder="Search PIN, name, email or mobile…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="h-9 w-full sm:w-72" data-testid={`${testIdPrefix}-deleted-search`} />
          {!deleted ? <Spinner /> : (deleted.items || []).length === 0 ? (
            <EmptyState title="No deleted citizens" subtitle={search ? "No deleted citizen matches the search." : "Nothing to restore for this municipality."} />
          ) : (
            <div className="rounded-md border overflow-hidden">
              <Table data-testid={`${testIdPrefix}-deleted-table`}>
                <TableHeader>
                  <TableRow>
                    <TableHead>Citizen</TableHead>
                    <TableHead>Property PIN</TableHead>
                    <TableHead>Deleted on</TableHead>
                    <TableHead>Deleted by</TableHead>
                    <TableHead>Last date to restore</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {deleted.items.map((c) => (
                    <TableRow key={c.id} data-testid={`${testIdPrefix}-deleted-row-${c.email}`}>
                      <TableCell>
                        <span className="block text-sm font-medium">{c.name}</span>
                        <span className="block text-[11px] text-muted-foreground">{c.email}{c.mobile ? ` · ${c.mobile}` : ""}</span>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{(c.pins || []).length ? (c.pins || []).slice(0, 2).join(", ") + ((c.pins || []).length > 2 ? ` +${c.pins.length - 2}` : "") : <span className="text-muted-foreground italic font-sans">Not linked</span>}</TableCell>
                      <TableCell className="text-sm tabular-nums">{fmtDateIST(c.deleted_at)}</TableCell>
                      <TableCell className="text-sm">{c.deleted_by_name || "-"}</TableCell>
                      <TableCell><RestoreWindowBadge citizen={c} testId={`${testIdPrefix}-until-${c.email}`} /></TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setRestoreTarget(c)} data-testid={`${testIdPrefix}-restore-${c.email}`}>
                          <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 text-xs ml-1 text-destructive hover:text-destructive" onClick={() => { setPurgeTarget(c); setPurgeText(""); }}
                          data-testid={`${testIdPrefix}-purge-${c.email}`}>
                          <Trash2 className="h-3.5 w-3.5 mr-1" /> Permanently delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {deleted && deleted.total > 0 && (
            <PaginationBar page={deleted.page || page} limit={limit} total={deleted.total} onPageChange={setPage}
              onLimitChange={(n) => { setLimit(n); setPage(1); }} testId={`${testIdPrefix}-deleted-pagination`} />
          )}
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-heading flex items-center gap-2"><ScrollText className="h-4 w-4" /> Recent citizen activity</CardTitle>
          <CardDescription className="text-xs">Permission changes, deletions, restores and permanent deletions — who did what and when (IST). The full history is in the municipality's Audit Trail.</CardDescription>
        </CardHeader>
        <CardContent className="p-4 pt-2">
          {audit === null ? <Spinner /> : audit.length === 0 ? (
            <p className="text-xs text-muted-foreground" data-testid={`${testIdPrefix}-audit-empty`}>No citizen activity recorded yet.</p>
          ) : (
            <ul className="divide-y" data-testid={`${testIdPrefix}-audit-list`}>
              {audit.map((a) => (
                <li key={a.id} className="py-2 flex items-start gap-3" data-testid={`${testIdPrefix}-audit-row`}>
                  <span className="h-7 w-7 rounded-md bg-muted text-muted-foreground flex items-center justify-center shrink-0"><UsersIcon className="h-3.5 w-3.5" /></span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm"><span className="font-medium">{a.label}</span> <span className="text-muted-foreground">· {a.user_name || "—"}</span></p>
                    {a.summary && <p className="text-xs text-muted-foreground">{a.summary}</p>}
                  </div>
                  <span className="text-[11px] text-muted-foreground tabular-nums whitespace-nowrap">{fmtDateTime(a.created_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!restoreTarget} onOpenChange={(v) => !v && setRestoreTarget(null)}>
        <AlertDialogContent data-testid={`${testIdPrefix}-restore-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Restore "{restoreTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              {restoreTarget && !restoreTarget.restore_window_open ? "The municipality's restore window has closed — as Super Admin you can still restore. " : ""}
              The account comes back {restoreTarget?.was_active_before_delete === false ? "inactive" : "active"} with its property link{(restoreTarget?.property_count || 0) === 1 ? "" : "s"} intact. Recorded in the Audit Trail.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testIdPrefix}-restore-cancel`}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmRestore(); }} disabled={busy} data-testid={`${testIdPrefix}-restore-confirm`}>{busy ? "Restoring…" : "Restore"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!purgeTarget} onOpenChange={(v) => { if (!v) { setPurgeTarget(null); setPurgeText(""); } }}>
        <AlertDialogContent data-testid={`${testIdPrefix}-purge-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-destructive">Permanently delete "{purgeTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Removes the login account for good and clears the citizen link on {purgeTarget?.property_count || 0} propert{purgeTarget?.property_count === 1 ? "y" : "ies"}. Property records, demands and receipts are untouched. This cannot be undone — type DELETE to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input value={purgeText} onChange={(e) => setPurgeText(e.target.value)} placeholder="DELETE" autoComplete="off" data-testid={`${testIdPrefix}-purge-input`} />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testIdPrefix}-purge-cancel`}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmPurge(); }} disabled={busy || purgeText.trim().toUpperCase() !== "DELETE"}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid={`${testIdPrefix}-purge-confirm`}>
              {busy ? "Deleting…" : "Permanently delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
